'use client';

import React, { useEffect, useState } from 'react';
import { backupsService } from '@/lib/api/backupsService';
import { getApiBaseUrl } from '@/lib/api/apiClient';
import { getToken } from '@/utils/auth';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';

const BackupsManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [backups, setBackups] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Estados para el modal de descarga
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [generatingSqlFromBackup, setGeneratingSqlFromBackup] = useState(false);
  const [downloadingFormat, setDownloadingFormat] = useState<'bku' | 'sql' | null>(null);
  
  // Estados para el modal de confirmación
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmVariant, setConfirmVariant] = useState<'danger' | 'primary'>('primary');

  const load = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    const res = await backupsService.list();
    if (res.data) setBackups(res.data);
    else setError(res.error || 'Error al cargar backups');
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const handleGenerate = () => {
    setConfirmTitle('Generar Backup');
    setConfirmMessage('¿Deseas generar un nuevo backup de la base de datos?');
    setConfirmVariant('primary');
    setConfirmAction(() => async () => {
      setConfirmModalOpen(false);
      setGenerating(true);
      setError(null);
      setSuccessMessage(null);

      try {
        const res = await backupsService.generate();
        if (res.data && res.data.success) {
          setSuccessMessage(res.data.message || 'Backup generado exitosamente');
          setTimeout(() => {
            load();
          }, 1000);
        } else {
          setError(res.error || 'Error al generar backup');
        }
      } catch (err) {
        setError('Error al generar backup. Por favor, intenta nuevamente.');
      } finally {
        setGenerating(false);
      }
    });
    setConfirmModalOpen(true);
  };


  const handleDelete = (filename: string) => {
    setConfirmTitle('Eliminar Backup');
    setConfirmMessage(`¿Estás seguro de que deseas eliminar el backup "${filename}"?`);
    setConfirmVariant('danger');
    setConfirmAction(() => async () => {
      setConfirmModalOpen(false);
      setError(null);
      setSuccessMessage(null);

      try {
        const res = await backupsService.remove(filename);
        if (res.data && res.data.ok) {
          setSuccessMessage(res.data.message || 'Backup eliminado exitosamente');
          load();
        } else {
          setError(res.error || 'Error al eliminar backup');
        }
      } catch (err) {
        setError('Error al eliminar backup. Por favor, intenta nuevamente.');
      }
    });
    setConfirmModalOpen(true);
  };

  const openDownloadModal = (filename: string) => {
    setSelectedBackup(filename);
    setDownloadModalOpen(true);
  };

  const closeDownloadModal = () => {
    setDownloadModalOpen(false);
    setSelectedBackup(null);
    setGeneratingSqlFromBackup(false);
  };

  const handleDownloadBku = async (filename: string) => {
    setDownloadingFormat('bku');
    const base = getApiBaseUrl();
    const token = getToken();
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const response = await fetch(`${base}/backups/${encodeURIComponent(filename)}`, {
        credentials: 'include',
        headers: authHeaders,
      });
      if (!response.ok) {
        setError(response.status === 401 ? 'No autorizado. Inicia sesión de nuevo.' : `Error ${response.status} al descargar.`);
        setDownloadingFormat(null);
        closeDownloadModal();
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Error al descargar el archivo. Comprueba la conexión.');
    } finally {
      setDownloadingFormat(null);
      closeDownloadModal();
    }
  };

  const handleDownloadSql = async (filename: string) => {
    setDownloadingFormat('sql');
    setError(null);
    
    // Primero verificar si ya existe un archivo .sql
    const sqlFilename = filename.replace('.bku', '.sql');
    const base = getApiBaseUrl();
    
    const token = getToken();
    const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

    // Intentar descargar directamente si existe (con credenciales para evitar 401)
    try {
      const response = await fetch(`${base}/backups/${encodeURIComponent(sqlFilename)}`, {
        credentials: 'include',
        headers: authHeaders,
      });
      if (response.ok) {
        // El archivo SQL ya existe, descargarlo
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = sqlFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setDownloadingFormat(null);
        closeDownloadModal();
        return;
      }
    } catch (e) {
      // Continuar para generar el SQL
    }

    // Si no existe, generarlo desde el backup
    setGeneratingSqlFromBackup(true);

    try {
      const res = await backupsService.generateSqlFromBackup(filename);
      if (res.data && res.data.success && res.data.filename) {
        const generatedFilename = res.data.filename;
        setSuccessMessage(res.data.message || 'Archivo SQL generado exitosamente');
        // Descargar el archivo generado
        setTimeout(async () => {
          try {
            const downloadResponse = await fetch(`${base}/backups/${encodeURIComponent(generatedFilename)}`, {
              credentials: 'include',
              headers: authHeaders,
            });
            if (downloadResponse.ok) {
              const blob = await downloadResponse.blob();
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = generatedFilename;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } else {
              setError(downloadResponse.status === 401 ? 'No autorizado al descargar.' : `Error ${downloadResponse.status} al descargar.`);
            }
          } catch (e) {
            setError('Error al descargar el archivo generado.');
          }
          setDownloadingFormat(null);
          closeDownloadModal();
          load();
        }, 500);
      } else {
        setError(res.error || 'Error al generar archivo SQL');
        setDownloadingFormat(null);
      }
    } catch (err) {
      setError('Error al generar archivo SQL. Por favor, intenta nuevamente.');
      setDownloadingFormat(null);
    } finally {
      setGeneratingSqlFromBackup(false);
    }
  };

  const downloadUrl = (filename: string) => {
    const base = getApiBaseUrl();
    return `${base}/backups/${filename}`;
  };

  const formatFilename = (filename: string): string => {
    // Formato: YYYY-MM-DD HH_MM_SS.bku
    // Convertir a formato más legible: YYYY-MM-DD HH:MM:SS
    try {
      const nameWithoutExt = filename.replace(/\.(bku|sql)$/, '');
      const [date, time] = nameWithoutExt.split(' ');
      const formattedTime = time.replace(/_/g, ':');
      return `${date} ${formattedTime}`;
    } catch {
      return filename;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Backups</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestiona los respaldos de la base de datos. Descarga o elimina respaldos disponibles.
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="primary" 
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generando...' : 'Generar Backup'}
          </Button>
          <Button variant="secondary" onClick={load} disabled={loading || generating}>
            Recargar
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} />}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {successMessage}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Backups Disponibles</h2>
          <p className="text-sm text-gray-500 mt-1">
            Los backups están ordenados por fecha (más recientes primero)
          </p>
        </div>
        
        {backups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p className="text-sm">No hay backups disponibles.</p>
            <p className="text-xs mt-2">Genera un backup para comenzar.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {backups.map((b) => (
              <div key={b} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">
                    {formatFilename(b)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Respaldo General de la Base de Datos • Formato (.bku o .sql)
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => openDownloadModal(b)}
                    disabled={downloadingFormat !== null}
                  >
                    {downloadingFormat === 'bku' && selectedBackup === b ? 'Descargando...' : 
                     downloadingFormat === 'sql' && selectedBackup === b ? 'Generando...' : 
                     'Descargar'}
                  </Button>
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => handleDelete(b)}
                    disabled={downloadingFormat !== null}
                  >
                    Eliminar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de descarga */}
      <Modal
        isOpen={downloadModalOpen}
        onClose={closeDownloadModal}
        confirmOnExit={false}
        title="Opciones de Descarga"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={closeDownloadModal}>
              Cancelar
            </Button>
          </div>
        }
      >
        {selectedBackup && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900 font-medium mb-2">
                Backup seleccionado:
              </p>
              <p className="text-sm text-blue-800">{formatFilename(selectedBackup)}</p>
              <p className="text-xs text-blue-600 mt-1">{selectedBackup}</p>
            </div>

            <div className="space-y-3">
              <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      Descargar como .bku (Comprimido)
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Archivo ZIP comprimido con el dump completo de la base de datos. 
                      Incluye metadatos y es el formato recomendado para respaldos completos.
                    </p>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleDownloadBku(selectedBackup)}
                      disabled={downloadingFormat !== null || generatingSqlFromBackup}
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      }
                    >
                      {downloadingFormat === 'bku' ? 'Descargando...' : 'Descargar .bku'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      Descargar como .sql (Script SQL)
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Archivo SQL plano con los comandos para restaurar la base de datos. 
                      Útil para importar en otros sistemas o revisar el contenido.
                    </p>
                    {generatingSqlFromBackup ? (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
                        <span>Generando archivo SQL...</span>
                      </div>
                    ) : (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleDownloadSql(selectedBackup)}
                        disabled={downloadingFormat !== null}
                        icon={
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        }
                      >
                        {downloadingFormat === 'sql' ? 'Descargando...' : 'Descargar .sql'}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mt-4">
                <ErrorMessage message={error} />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal de confirmación */}
      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        title={confirmTitle}
        message={confirmMessage}
        variant={confirmVariant}
        confirmText={confirmVariant === 'danger' ? 'Eliminar' : 'Generar'}
        cancelText="Cancelar"
        loading={generating}
      />
    </div>
  );
};

export default BackupsManagement;
